import { useMemo } from 'react'
import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { useAppSelector } from 'hooks/reduxHooks'
import { isStatusMatchLabPayload } from 'loyalty/statusMatch/statusMatchTypes'
import type { StatusMatchCampaignConfig } from 'loyalty/statusMatch/statusMatchTypes'

/**
 * Returns the active Status Match campaign for the user's region, regardless of slug.
 * Used by the account page/drawer to surface the campaign outside the landing page.
 * Returns undefined if no active campaign exists or the user is outside allowed regions.
 *
 * Note: Lab `enabled` is the kill switch — when the campaign ends, toggle the flag off.
 * There's no date-based filtering here.
 */
function useActiveStatusMatchCampaign(): StatusMatchCampaignConfig | undefined {
  const flag = useLabFeatureFlag(LabFeatureFlags.enableStatusMatch)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  return useMemo(() => {
    if (!flag?.enabled) return undefined
    const payload = flag.value
    if (!isStatusMatchLabPayload(payload)) return undefined
    if (!payload.allowedRegions.includes(regionCode)) return undefined

    return { config: payload }
  }, [flag, regionCode])
}

export default useActiveStatusMatchCampaign
